import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";

/** ResetPassword — user clicks the emailed link with `?token=xxx`, chooses a
 *  new password, then is redirected to /auth to sign in. Token is single-use
 *  and expires 30 min after generation. */
export default function ResetPassword() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error(t("auth.resetNoToken", "Reset token missing from URL"));
  }, [token, t]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error(t("auth.pwTooShort", "Password must be at least 8 characters"));
    if (pw !== pw2) return toast.error(t("auth.pwMismatch", "Passwords do not match"));
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success(t("auth.pwUpdated", "Password updated"));
      setTimeout(() => nav("/auth"), 2000);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-bg text-white flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="mb-6">
          <div className="w-14 h-14 rounded-2xl bg-amber-gold/15 border border-amber-gold/40 flex items-center justify-center mb-4">
            {done ? <CheckCircle2 size={22} className="text-electric-teal" /> : <KeyRound size={22} className="text-amber-gold" />}
          </div>
          <h1 className="text-2xl font-black tracking-tight">
            {done ? t("auth.pwUpdated", "Password updated") : t("auth.setNewPassword", "Set a new password")}
          </h1>
          {!done && <p className="text-sm text-ink-textDim mt-1">{t("auth.setNewDesc", "Pick a strong password — at least 8 characters.")}</p>}
        </div>
        {done ? (
          <div className="bg-electric-teal/10 border border-electric-teal/40 rounded-2xl p-4 text-sm" data-testid="reset-done">
            {t("auth.resetDoneMsg", "Redirecting you to the login screen…")}
            <div className="mt-3"><Link to="/auth" className="text-electric-teal font-black">{t("auth.signInNow", "Sign in now →")}</Link></div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <input
              type="password"
              required minLength={8}
              value={pw} onChange={(e) => setPw(e.target.value)}
              placeholder={t("auth.newPasswordPh", "New password")}
              className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-4 py-3 text-sm outline-none focus:border-amber-gold"
              data-testid="reset-password-new"
              autoComplete="new-password"
            />
            <input
              type="password"
              required minLength={8}
              value={pw2} onChange={(e) => setPw2(e.target.value)}
              placeholder={t("auth.confirmPasswordPh", "Confirm new password")}
              className="w-full bg-ink-surface2 border border-ink-border rounded-xl px-4 py-3 text-sm outline-none focus:border-amber-gold"
              data-testid="reset-password-confirm"
              autoComplete="new-password"
            />
            <button
              type="submit"
              disabled={busy || !token}
              className="w-full bg-amber-gold text-obsidian rounded-full py-3.5 font-black flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="reset-submit"
            >
              {busy && <Loader2 className="animate-spin" size={16} />}
              {t("auth.updatePassword", "Update password")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
